import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';

import {
  createCombineCondition,
  isCombineCondition,
} from '@/types/advanceFilter';

export const toJsonText = function (query: Condition): string {
  if (!query) {
    return '';
  }

  return JSON.stringify(query, (key, value) => {
    if (isCombineCondition(value) && !value.conditions?.length) {
      return undefined;
    }
    return value;
  });
};

const escapeLuceneValue = (value: string): string => {
  if (/[\s+\-!(){}[\]^"~*?:\\/&|]/.test(value)) {
    return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  }
  return value;
};

const escapeLuceneWildcard = (value: string): string =>
  value.replace(/([\s+\-!(){}[\]^"~*?:\\/&|])/g, '\\$1');

/**
 * Whether the whole expression is one parenthesised group, rather than merely
 * starting with one — `(a OR b)` is, `(a OR b) AND (c OR d)` is not. Quoted
 * spans are skipped so a bracket inside a value cannot close the group early.
 */
const isSingleGroup = (expression: string) => {
  if (!expression.startsWith('(')) {
    return false;
  }

  let depth = 0;
  let quoted = false;
  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];
    if (char === '"') {
      quoted = !quoted;
    } else if (!quoted && char === '(') {
      depth++;
    } else if (!quoted && char === ')') {
      depth--;
      if (depth === 0) {
        return i === expression.length - 1;
      }
    }
  }

  return false;
};

/**
 * Parenthesises an expression that alternates before it becomes one side of an
 * AND. Lucene binds AND tighter than OR, so `a AND b:1 OR b:2` reads as
 * `(a AND b:1) OR b:2` — the other side would apply to only the first
 * alternative, and the query would match records it was meant to exclude.
 *
 * An expression that is already one group is left alone: this runs again on its
 * own previous output every time a filter is edited, and wrapping unconditionally
 * would add a layer of brackets each time.
 */
/**
 * Whether the expression holds more than one clause at its top level. Anything
 * separated by whitespace out here is: the grammar joins alternatives with
 * `OR`, `or` or `||`, and joins bare clauses with nothing at all, so a space is
 * itself a separator. Quoted spans and bracketed groups are skipped, since the
 * whitespace inside them belongs to a single clause.
 */
const hasSeveralClauses = (expression: string) => {
  let depth = 0;
  let quoted = false;
  for (const char of expression.trim()) {
    if (char === '"') {
      quoted = !quoted;
    } else if (!quoted && char === '(') {
      depth++;
    } else if (!quoted && char === ')') {
      depth--;
    } else if (!quoted && depth === 0 && /\s/.test(char)) {
      return true;
    }
  }

  return false;
};

const groupForAnd = (expression: string) =>
  hasSeveralClauses(expression) && !isSingleGroup(expression)
    ? `(${expression})`
    : expression;

const conditionToLucene = (condition: Condition | undefined): string => {
  if (!condition) {
    return '';
  }

  if (isCombineCondition(condition)) {
    const subQueries = (condition.conditions || [])
      .map(conditionToLucene)
      .filter(Boolean);

    if (subQueries.length === 0) {
      return '';
    }
    if (subQueries.length === 1) {
      return subQueries[0];
    }

    const operator =
      condition.operator === ConditionOperatorEnum.Or ? 'OR' : 'AND';
    const parts = operator === 'AND' ? subQueries.map(groupForAnd) : subQueries;
    return parts.join(` ${operator} `);
  }

  const { property, value: values } = condition;
  if (!property || !values?.length) {
    return '';
  }

  switch (condition.operator) {
    case ConditionOperatorEnum.In:
    case ConditionOperatorEnum.Equals: {
      return values
        .map((v) => `${property}:${escapeLuceneValue(v)}`)
        .join(' OR ');
    }

    case ConditionOperatorEnum.NotIn:
    case ConditionOperatorEnum.NotEquals: {
      if (values.length === 1) {
        return `NOT ${property}:${escapeLuceneValue(values[0])}`;
      }
      return `NOT ${property}:(${values.map(escapeLuceneValue).join(' OR ')})`;
    }

    case ConditionOperatorEnum.Contains:
    case ConditionOperatorEnum.ContainsAnyOf: {
      const expr = values
        .map((v) => `${property}:*${escapeLuceneWildcard(v)}*`)
        .join(' OR ');
      return values.length > 1 ? `(${expr})` : expr;
    }

    // Every term has to match, so these are joined with AND rather than the OR
    // the any-of family uses.
    case ConditionOperatorEnum.ContainsAllOf: {
      const expr = values
        .map((v) => `${property}:*${escapeLuceneWildcard(v)}*`)
        .join(' AND ');
      return values.length > 1 ? `(${expr})` : expr;
    }

    case ConditionOperatorEnum.NotContains:
    case ConditionOperatorEnum.ContainsNoneOf: {
      return values
        .map((v) => `NOT ${property}:*${escapeLuceneWildcard(v)}*`)
        .join(' AND ');
    }

    default:
      return '';
  }
};

const collectProperties = (condition: Condition | undefined): string[] => {
  if (!condition) {
    return [];
  }
  if (isCombineCondition(condition)) {
    return (condition.conditions || []).flatMap(collectProperties);
  }
  return condition.property ? [condition.property] : [];
};

const stripPropertyTerms = (filter: string, properties: string[]): string => {
  let result = filter;
  for (const prop of properties) {
    const escaped = RegExp.escape(prop);
    result = result.replace(
      new RegExp(`(NOT\\s+)?${escaped}:(\\([^)]*\\)|"[^"]*"|[^\\s)]+)`, 'g'),
      ''
    );
  }
  let cleaned = result;
  let prev;
  do {
    prev = cleaned;
    cleaned = cleaned
      .replace(/\(\s*(AND|OR)?\s*\)/g, '')
      .replace(/\s+(AND|OR)\s+(AND|OR)\s+/g, ' $1 ')
      .replace(/^\s*(AND|OR)\s+/, '')
      .replace(/\s+(AND|OR)\s*$/, '');
  } while (cleaned !== prev);
  return cleaned.trim();
};

/**
 * The values a property is matched against, split by whether the clause negates.
 *
 * Both halves are read because {@link conditionToLucene} writes both: a negated
 * condition becomes a `NOT` clause, and a filter surface that could not read it
 * back would drop the chip while the query kept filtering.
 */
const extractPropertyValues = (
  filter: string,
  property: string
): { positive: string[]; negated: string[] } => {
  const escaped = RegExp.escape(property);
  const positive: string[] = [];
  const negated: string[] = [];

  const collect = (negatedClause: boolean) => {
    const prefix = negatedClause ? 'NOT\\s+' : '(?<!NOT\\s+)';
    const into = negatedClause ? negated : positive;

    const groupedPattern = new RegExp(`${prefix}${escaped}:\\(([^)]*)\\)`, 'g');
    let match;
    while ((match = groupedPattern.exec(filter)) !== null) {
      match[1]
        .split(/\s+OR\s+/)
        .map((v) => v.replace(/^"|"$/g, '').trim())
        .filter(Boolean)
        .forEach((v) => into.push(v));
    }

    const singlePattern = new RegExp(
      `${prefix}${escaped}:(?!\\()("([^"]*)"|([^\\s)]+))`,
      'g'
    );
    while ((match = singlePattern.exec(filter)) !== null) {
      const val = match[2] ?? match[3];
      if (val) {
        into.push(val);
      }
    }
  };

  collect(false);
  collect(true);

  return { positive, negated };
};

/**
 * `readNegated` is off by default because a surface that cannot show the operator
 * would render an exclusion as a selection: the value would appear picked, and
 * picking another would widen the exclusion rather than narrow the results. Only
 * a caller that shows the operator alongside the values should turn it on.
 */
export const fromLucene = (
  filter: string,
  managedProperties: string[],
  { readNegated = false }: { readNegated?: boolean } = {}
): Condition => {
  const conditions = managedProperties.reduce<Condition[]>((acc, prop) => {
    const { positive, negated } = extractPropertyValues(filter, prop);
    // One condition per property: a filter chip edits a single condition, and a
    // second one on the same property would be dropped the moment it is touched.
    // A query naming both directions keeps the including half.
    if (positive.length) {
      acc.push({
        property: prop,
        operator: ConditionOperatorEnum.In,
        value: positive,
      });
    } else if (readNegated && negated.length) {
      acc.push({
        property: prop,
        operator: ConditionOperatorEnum.NotIn,
        value: negated,
      });
    }
    return acc;
  }, []);

  return createCombineCondition(conditions);
};

export const toLucene = (
  condition: Condition | undefined,
  existingFilter?: string,
  managedProperties?: string[]
): string => {
  const generated = conditionToLucene(condition);

  if (!existingFilter?.trim()) {
    return generated;
  }

  const properties = managedProperties?.length
    ? managedProperties
    : [...new Set(collectProperties(condition))];

  const cleanedExisting = properties.length
    ? stripPropertyTerms(existingFilter, properties)
    : existingFilter.trim();

  if (!generated && !cleanedExisting) {
    return '';
  }
  if (!generated) {
    return cleanedExisting;
  }
  if (!cleanedExisting) {
    return generated;
  }
  return `${groupForAnd(cleanedExisting)} AND ${groupForAnd(generated)}`;
};
